import React from 'react'
import { ArrowUpRight, ArrowDownRight, Minus, Zap, UserPen, RefreshCw, Settings2 } from 'lucide-react'
import DashboardCard from '@/components/dashboard/DashboardCard'
import HotelChip from '@/components/groups/HotelChip'
import { cn } from '@/lib/utils'
import { formatMoney } from '@/lib/format'
import { spansHotels } from '@/lib/reporting-scope'

interface ActivityEntry {
  id: number
  /**
   * Which hotel the change landed on. Always sent by the backend (never conditional on grouping),
   * because a change is stamped with the property that owns the room — which is what keeps
   * publishing per listing. Only rendered once the feed actually spans hotels.
   */
  propertyId?: number
  propertyName?: string | null
  roomType: string
  ratePlan: string
  date: string
  oldRate: number
  newRate: number
  changePct: number
  source: string
  createdAt: string
}

interface RecentActivityCardProps {
  entries: ActivityEntry[]
  currency: string
  /** The group's lead, so its rows wear the crown rather than a plain hotel chip. */
  leadPropertyId?: number | null
}

function timeAgo(iso: string): string {
  const now = new Date()
  const then = new Date(iso)
  const diffMs = now.getTime() - then.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return then.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

const sourceConfig: Record<string, { icon: React.ElementType; label: string; color: string; bg: string }> = {
  computation: { icon: Zap, label: 'Auto', color: 'text-blue-600', bg: 'bg-blue-50' },
  manual_override: { icon: UserPen, label: 'Manual', color: 'text-purple-600', bg: 'bg-purple-50' },
  bulk_edit: { icon: RefreshCw, label: 'Bulk', color: 'text-amber-600', bg: 'bg-amber-50' },
  rule_change: { icon: Settings2, label: 'Rule', color: 'text-teal-600', bg: 'bg-teal-50' },
}

export default function RecentActivityCard({ entries, currency, leadPropertyId }: RecentActivityCardProps) {
  // One pass prices every hotel in a unit, so at a lead this one feed describes the group. Label the
  // rows only when they actually span hotels — nothing to disambiguate otherwise.
  const showHotel = spansHotels(entries)

  return (
    <DashboardCard
      title="Recent Activity"
      description={showHotel ? 'Latest rate changes across every listing' : 'Latest rate changes'}
      help={{
        content: showHotel
          ? 'The ten most recent rate changes across every listing, each named with the listing it landed on and what caused it.'
          : 'The ten most recent rate changes, and what caused each one.',
        learnMore: '/guides/history',
      }}
      padded={false}
    >
      {entries.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <p className="text-sm text-muted-foreground">No recent activity</p>
        </div>
      ) : (
        <div className="divide-y divide-border/30">
          {entries.map((entry) => {
            const increased = entry.newRate > entry.oldRate
            const decreased = entry.newRate < entry.oldRate
            const config = sourceConfig[entry.source] || sourceConfig.computation
            const SourceIcon = config.icon
            const changePct = entry.changePct

            return (
              <div key={entry.id} className="flex items-center gap-3 px-5 py-3 transition-colors hover:bg-muted/20">
                {/* Source icon */}
                <div className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', config.bg)}>
                  <SourceIcon className={cn('h-3.5 w-3.5', config.color)} />
                </div>

                {/* Details */}
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="min-w-0 break-words text-sm font-medium text-foreground sm:truncate" title={entry.roomType}>
                      {entry.roomType}
                    </span>
                    <span className={cn(
                      'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold',
                      config.bg, config.color
                    )}>
                      {config.label}
                    </span>
                  </div>
                  <div className="flex min-w-0 items-center gap-2">
                    <p className="truncate text-xs text-muted-foreground" title={`${entry.date} · ${entry.ratePlan}`}>
                      {entry.date} · {timeAgo(entry.createdAt)}
                    </p>
                    {showHotel && (
                      <HotelChip
                        name={entry.propertyName}
                        propertyId={entry.propertyId}
                        isLead={leadPropertyId != null && entry.propertyId === leadPropertyId}
                      />
                    )}
                  </div>
                </div>

                {/* Rate change */}
                <div className="shrink-0 text-right">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">
                      {formatMoney(entry.oldRate, currency)}
                    </span>
                    {increased ? (
                      <ArrowUpRight className="h-3.5 w-3.5 text-emerald-500" />
                    ) : decreased ? (
                      <ArrowDownRight className="h-3.5 w-3.5 text-rose-500" />
                    ) : (
                      <Minus className="h-3.5 w-3.5 text-slate-400" />
                    )}
                    <span className={cn(
                      'text-sm font-semibold',
                      increased ? 'text-emerald-600' : decreased ? 'text-rose-600' : 'text-foreground'
                    )}>
                      {formatMoney(entry.newRate, currency)}
                    </span>
                  </div>
                  <p className={cn(
                    'text-[10px] font-medium text-right',
                    increased ? 'text-emerald-500' : decreased ? 'text-rose-500' : 'text-muted-foreground'
                  )}>
                    {increased ? '+' : ''}{changePct.toFixed(1)}%
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </DashboardCard>
  )
}
